import { useState } from 'react';
import { Box, Typography, Button, Snackbar, Alert } from '@mui/material';
import { Add, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eventService } from '../services/events.service';
import { Event, EventCategory, EventStatus } from '../types';
import { EventsTable, EventsToolbar } from '../components/events/EventsTable';
import { EventForm } from '../components/events/EventForm';

export default function Events() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<EventStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<EventCategory | ''>('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const queryClient = useQueryClient();

  const {
    data: eventsData,
    isLoading,
  } = useQuery({
    queryKey: ['events', { page, limit, search, statusFilter, categoryFilter }],
    queryFn: () => {
      const filters: Record<string, any> = { page, limit, search };
      if (statusFilter) filters.status = statusFilter;
      if (categoryFilter) filters.category = categoryFilter;
      return eventService.getList(filters);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>) => eventService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      setIsFormOpen(false);
      setEditingEvent(null);
      setNotification({ message: 'Событие успешно создано', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при создании события', type: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Event> }) => eventService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      setIsFormOpen(false);
      setEditingEvent(null);
      setNotification({ message: 'Событие успешно обновлено', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при обновлении события', type: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => eventService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      setNotification({ message: 'Событие удалено', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при удалении события', type: 'error' });
    },
  });

  const publishMutation = useMutation({
    mutationFn: (id: number) => eventService.publish(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      setNotification({ message: 'Событие опубликовано', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при публикации события', type: 'error' });
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: (id: number) => eventService.unpublish(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      setNotification({ message: 'Событие снято с публикации', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при снятии с публикации', type: 'error' });
    },
  });

  const filteredEvents = eventsData?.data || [];
  const total = eventsData?.pagination?.total || 0;

  const handleCreate = () => {
    setEditingEvent(null);
    setIsFormOpen(true);
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
    setIsFormOpen(true);
  };

  const handleSubmit = async (data: any) => {
    if (editingEvent) {
      await updateMutation.mutateAsync({ id: editingEvent.id, data });
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Вы уверены, что хотите удалить это событие?')) {
      deleteMutation.mutate(id);
    }
  };

  const handlePublish = (id: number) => {
    publishMutation.mutate(id);
  };

  const handleUnpublish = (id: number) => {
    unpublishMutation.mutate(id);
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontFamily: 'Noto Serif' }}>
          События
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Добавить событие
        </Button>
      </Box>

      <EventsToolbar
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        statusFilter={statusFilter}
        onStatusFilterChange={(value) => {
          setStatusFilter(value);
          setPage(1);
        }}
        categoryFilter={categoryFilter}
        onCategoryFilterChange={(value) => {
          setCategoryFilter(value);
          setPage(1);
        }}
      />

      <EventsTable
        events={filteredEvents}
        loading={isLoading}
        total={total}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onPublish={handlePublish}
        onUnpublish={handleUnpublish}
      />

      <EventForm
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleSubmit}
        initialData={editingEvent as any}
        loading={createMutation.isPending || updateMutation.isPending}
        error={createMutation.error?.message || updateMutation.error?.message}
      />

      <Snackbar
        open={!!notification}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification?.type === 'success' ? 'success' : 'error'}
          sx={{ width: '100%' }}
          icon={notification?.type === 'success' ? <CheckCircle /> : <ErrorIcon />}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
